import { useEffect, useRef } from "react";
import api from "@/lib/api";

/**
 * ZIP → city/state/county autofill hook.
 *
 * Usage:
 *   useZipAutofill(form.zip_code, {
 *     city: form.city,
 *     state: form.state,
 *     county: form.county,
 *   }, ({ city, state, county }) => {
 *     setForm((s) => ({
 *       ...s,
 *       city: city ?? s.city,
 *       state: state ?? s.state,
 *       county: county ?? s.county,
 *     }));
 *   });
 *
 * Behavior:
 *  - Fires 350ms after the user finishes typing a 5-digit ZIP
 *  - Calls the public /api/geo/zip/{zipcode} endpoint
 *  - Only passes back values for fields that are CURRENTLY EMPTY
 *    (respects user-typed data — never overwrites)
 *  - Silent on failure (bogus ZIP just leaves the form alone)
 */
export function useZipAutofill(zip, currentValues, applyFill) {
  const lastZipRef = useRef("");
  const timerRef = useRef(null);

  useEffect(() => {
    const zc = (zip || "").trim();

    // Only trigger on a fresh 5-digit numeric ZIP we haven't just resolved.
    if (!/^\d{5}$/.test(zc)) return;
    if (zc === lastZipRef.current) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/geo/zip/${zc}`);
        if (!data?.ok) return;
        lastZipRef.current = zc;

        // Only fill fields that are currently empty. Never overwrite user-typed data.
        const patch = {};
        if (data.city && !(currentValues?.city || "").trim()) patch.city = data.city;
        if (data.state && !(currentValues?.state || "").trim()) patch.state = data.state;
        if (data.county && !(currentValues?.county || "").trim()) patch.county = data.county;
        if (Object.keys(patch).length > 0) applyFill(patch);
      } catch {
        // Silent — bogus ZIP just leaves the form alone.
      }
    }, 350);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // We intentionally omit `currentValues` + `applyFill` from deps — we snapshot them at
    // fire time. Adding them causes an infinite-loop when the applyFill triggers a rerender.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zip]);
}
